import { urls } from '~/preloaded';
import settings from '~/settings';
import { BATTLE_TYPES, SEASON_TYPES, REQUEST_STATUSES } from '~/constants';
import { get, patch, post } from '~/helpers/api';
import { showInviteAcceptDialog, showSendApplicationDialog } from '~/helpers/dialogs';
import { getLastSeasonOfType } from '~/helpers/ladder';
import Loader from '~/helpers/loader';
import { t } from '~/helpers/localization';
import { ROLE_NAMES } from '~/roles';
import { currentClanSelector } from '~/store/selectors/currentAccountSelector';
import { currentAccountActions } from '~/store/slices/currentAccountSlice';

import { ENTRY_SOURCE } from '~/store/slices/settingsSlice';
import { syncAccountInfoThunk } from '~/store/thunks/accountThunks';

import {
  sendAcceptInviteClanIsDisbandedErrorNotification,
  sendAcceptInviteClanIsFullErrorNotification,
  sendAcceptInviteDeletedStatusNotification,
  sendAcceptInviteErrorWithClanInfoNotification,
  sendAcceptInviteNotification,
  sendAcceptInviteWithExpiredStatusNotification,
  sendAcceptInviteYouAreInClanCooldownErrorNotification,
  sendAcceptInviteYouAreInOtherClanErrorNotification,
  sendCancelInviteErrorNotification,
  sendCancelInviteNotification,
  sendDeclineInviteErrorNotification,
  sendDeclineInviteNotification,
  sendSendInviteAlreadySentNotification,
  sendSendInviteClanIsFullErrorNotification,
  sendSendInviteDailyInvitesLimitReachedErrorNotification,
  sendSendInviteErrorWithPlayerInfoNotification,
  sendSendInviteNotification,
  sendSendInvitePermissionsErrorNotification,
  sendSendInvitePlayerHasPermanentBanErrorNotification,
  sendSendInvitePlayerInOtherClanErrorNotification,
  sendSendInviteYouAreNotInClanNotification,
} from '~/web2ClientAPI/notifications';
import { actionsApplications } from '~/Actions/ActionApplications';
import { actionsRequests } from '~/Actions/ActionRequests';

import type { ClanInfoType } from './ActionClanProfile';

import type { Meta } from '~/Actions/ActionApplications';
import type { InferActionsType } from '~/Reducers';
import type { ACCOUNT_BAN_TYPES } from '~/constants';
import type { AxiosCustomError } from '~/helpers/api';
import type { AppAsyncThunk, IAppDispatch, RootState } from '~/store';
import type {
  IApiAcceptInviteError,
  IApiAcceptInviteResponse,
  IApiDeclineApplicationResponse,
  IApiDeclineInviteError,
  IApiError,
  IApiGetRecruitmentActiveInviteResponse,
  IApiSendInviteError,
  IApiSendInviteResponse,
} from '~/types/api';
import type { ClanInSearch, ClanPreloaded } from '~/types/declaration';
import type { Nullable } from '~/types/generics';

export const INVITES_CHANGE_PAGE = 'INVITES_CHANGE_PAGE';
export const INVITES_CHANGE_ORDER = 'INVITES_CHANGE_ORDER';
export const INVITES_TOGGLE_FETCHING = 'INVITES_TOGGLE_FETCHING';
export const INVITES_UPDATE_INVITES = 'INVITES_UPDATE_INVITES';
export const INVITES_UPDATE_ERROR = 'INVITES_UPDATE_ERROR';
export const INVITES_START_PROCESSING = 'INVITES_START_PROCESSING';
export const INVITES_PROCESSING_FAILED = 'INVITES_PROCESSING_FAILED';
export const INVITES_PROCESSING_SUCCESS = 'INVITES_PROCESSING_SUCCESS';

export type ActionsType = InferActionsType<typeof actionsInvites>;

export type ClanType = {
  color: string;
  id: number;
  is_active: boolean;
  leveling: number;
  members_count: number;
  name: string;
  tag: string;
};

export type IAccountStatistics = {
  abd: number;
  admg: number;
  aeb: number;
  afb: number;
  btl: number;
  rank: number;
  season_id: number;
  season_rank: Nullable<number>;
  wb: number;
};

export type IClanStatistics = {
  abd: number;
  abtl: number;
  admg: number;
  aeb: number;
  afb: number;
  awb: number;
};

type Sender = {
  id: number;
  role: string;
  name: string;
  clan_id: number;
};

export type IAccount = {
  bans: ACCOUNT_BAN_TYPES[];
  clan_id: Nullable<number>;
  id: number;
  in_clan_cooldown_till: string;
  is_banned: boolean;
  name: string;
};

export type IClanInvite = {
  account: IAccount;
  comment: Nullable<string>;
  created_at: string;
  expires_at: string;
  game: string;
  id: number;
  is_banned: boolean;
  is_hidden_statistics: boolean;
  sender: Sender;
  statistics: IAccountStatistics;
  status: REQUEST_STATUSES;
  updated_at: string;
};

export type ClanInvitationMinimal = {
  id: number;
  clan: {
    id: number;
    leveling: number;
    tag: string;
    name: string;
    color: string;
  };
};

export type IAccountInvite = {
  clan: ClanType;
  comment: Nullable<string>;
  created_at: string;
  expires_at: string;
  game: string;
  id: number;
  is_hidden_statistics: boolean;
  sender: Sender;
  statistics: IClanStatistics;
  status: string;
  updated_at: string;
};

export type IInvite = IClanInvite | IAccountInvite;

type UpdateInviteType = {
  _meta_: Meta;
  invites: IInvite[];
};

export const actionsInvites = {
  changePage: (page: number) =>
    ({
      type: INVITES_CHANGE_PAGE,
      page,
    }) as const,

  changeOrder: (order: string, isAsc: boolean) =>
    ({
      type: INVITES_CHANGE_ORDER,
      order,
      isAsc,
    }) as const,

  toggleFetching: () =>
    ({
      type: INVITES_TOGGLE_FETCHING,
    }) as const,

  updateInvites: (data: UpdateInviteType) =>
    ({
      type: INVITES_UPDATE_INVITES,
      data,
    }) as const,

  updateError: (error: IApiError) =>
    ({
      type: INVITES_UPDATE_ERROR,
      error,
    }) as const,

  startProcessing: (id: number) =>
    ({
      type: INVITES_START_PROCESSING,
      id,
    }) as const,

  processingSuccess: (id: number) =>
    ({
      type: INVITES_PROCESSING_SUCCESS,
      id,
    }) as const,

  processingFailed: (id: number) =>
    ({
      type: INVITES_PROCESSING_FAILED,
      id,
    }) as const,
};

const syncForNewClan = async (dispatch: IAppDispatch, clanId: number | null, skipNotification = false) => {
  const messages: Record<string, () => void> = skipNotification ? { joinClan: () => {} } : {};

  try {
    await dispatch(syncAccountInfoThunk(messages));
  } catch {
    if (clanId) {
      dispatch(
        currentAccountActions.updateAccountInfo({
          clanId,
          roleName: ROLE_NAMES.PRIVATE,
          activeInvitesCount: 0,
        }),
      );
    }
  }
};

const processAcceptError = async (
  dispatch: IAppDispatch,
  state: RootState,
  error: IApiAcceptInviteError,
  invite: ClanInvitationMinimal,
) => {
  const reason = error?.title?.toLowerCase();

  if (reason === 'account_already_in_clan') {
    sendAcceptInviteYouAreInOtherClanErrorNotification(invite.clan);
    const clanId = error?.additional_data?.clan_id || error?.additional_info?.clan_id || null;
    await syncForNewClan(dispatch, clanId);

    return;
  }

  if (reason === 'invite_is_not_active') {
    const inviteStatus = error?.additional_data?.status || error?.additional_info?.status;

    if (inviteStatus === REQUEST_STATUSES.EXPIRED) {
      sendAcceptInviteWithExpiredStatusNotification(invite.clan);
    } else if (inviteStatus === REQUEST_STATUSES.DELETED) {
      sendAcceptInviteDeletedStatusNotification(invite.clan);
    } else {
      sendAcceptInviteErrorWithClanInfoNotification(invite.clan);
    }

    dispatch(currentAccountActions.decreaseActiveEntriesCount());
    dispatch(actionsInvites.processingSuccess(invite.id));

    return;
  }

  if (reason === 'account_in_cooldown') {
    const cooldown = state.currentAccount.inClanCooldownTill;
    if (cooldown) {
      sendAcceptInviteYouAreInClanCooldownErrorNotification(invite.clan, cooldown);
    }
  } else if (reason === 'clan_already_disbanded') {
    sendAcceptInviteClanIsDisbandedErrorNotification(invite.clan);
  } else if (reason === 'clan_is_full') {
    sendAcceptInviteClanIsFullErrorNotification(invite.clan);
  } else {
    sendAcceptInviteErrorWithClanInfoNotification(invite.clan);
  }

  dispatch(actionsInvites.processingFailed(invite.id));
};

export const acceptInvite =
  (invite: ClanInvitationMinimal): AppAsyncThunk =>
  async (dispatch, getState) => {
    const state = getState();
    if (state.invites.isFetching) {
      return;
    }

    dispatch(actionsInvites.toggleFetching());

    const loader = new Loader();
    loader.start(dispatch);

    try {
      const url = `${urls.invites}${invite.id}/`;
      const payload = { status: REQUEST_STATUSES.ACCEPTED };

      const response = await patch<IApiAcceptInviteResponse & IApiAcceptInviteError>(url, payload);

      dispatch(actionsInvites.startProcessing(invite.id));

      if (response.title || response.status === 'error') {
        dispatch(actionsInvites.toggleFetching());
        await processAcceptError(dispatch, getState(), response, invite);
        return;
      }

      sendAcceptInviteNotification(invite.clan);

      await syncForNewClan(dispatch, invite.clan.id, true);
      dispatch(actionsInvites.toggleFetching());
    } catch (error: unknown) {
      const axiosError = error as AxiosCustomError<IApiAcceptInviteError>;

      dispatch(actionsInvites.toggleFetching());

      await processAcceptError(dispatch, getState(), axiosError?.data || null, invite);
    } finally {
      loader.stop(dispatch);
    }
  };

const processDeclineError = async (dispatch: IAppDispatch, error: IApiDeclineInviteError, invite: IAccountInvite) => {
  const reason = error?.additional_info?.reason?.toLowerCase();
  const title = error?.title?.toLowerCase();

  if (reason === 'account_already_in_clan') {
    const clanId = error?.additional_info?.clan_id || null;

    await syncForNewClan(dispatch, clanId);

    return;
  }

  if (reason === 'invite_is_not_active' || title === 'invite_is_not_active') {
    dispatch(currentAccountActions.decreaseActiveEntriesCount());
    dispatch(actionsInvites.processingSuccess(invite.id));

    return;
  }

  sendDeclineInviteErrorNotification();
  dispatch(actionsInvites.processingFailed(invite.id));
};

export const declineInvite =
  (invite: IAccountInvite): AppAsyncThunk<void> =>
  async (dispatch) => {
    dispatch(actionsInvites.startProcessing(invite.id));

    try {
      const url = `${urls.invites}${invite.id}/`;
      const payload = { status: REQUEST_STATUSES.DECLINED };

      const response = await patch<IApiDeclineApplicationResponse & IApiDeclineInviteError>(url, payload);

      if (response?.status === 'error') {
        await processDeclineError(dispatch, response, invite);
        return;
      }

      sendDeclineInviteNotification(invite.clan);

      dispatch(currentAccountActions.decreaseActiveEntriesCount());
      dispatch(actionsInvites.processingSuccess(invite.id));
    } catch (error: unknown) {
      const axiosError = error as AxiosCustomError<IApiDeclineInviteError>;

      await processDeclineError(dispatch, axiosError.data, invite);
    }
  };

export const fetchInvites =
  (withGlobalSpinner = false): AppAsyncThunk<void> =>
  async (dispatch, getState) => {
    const state = getState();
    if (state.invites.isFetching) {
      return;
    }

    let currentSeason = state.requests.requestsSeason;
    const battleType = state.requests.requestsBattleType;
    const clanSeasonType =
      battleType === BATTLE_TYPES.REGULAR_CVC
        ? SEASON_TYPES.REGULAR
        : battleType === BATTLE_TYPES.BRAWL_CVC
          ? SEASON_TYPES.BRAWL
          : null;

    dispatch(actionsInvites.toggleFetching());

    if (clanSeasonType && state.requests.requestsSeasonType !== clanSeasonType) {
      dispatch(actionsRequests.changeRequestsSeasonType(clanSeasonType));
      const lastSeasonId = getLastSeasonOfType(clanSeasonType)?.id;
      if (lastSeasonId) {
        currentSeason = lastSeasonId;
      }
      dispatch(actionsRequests.changeRequestsSeason(currentSeason));
    }

    const loader = withGlobalSpinner ? new Loader(t('Загружаем приглашения')) : null;
    loader?.start(dispatch);

    try {
      const currentAccount = state.currentAccount;
      const url = currentAccount.clanId ? urls.invitesClanList : urls.invitesAccountList;
      const limit = settings.entries.limit;
      const order = state.invites.sort.order;
      const query = {
        battle_type: clanSeasonType ? 'cvc' : state.requests.requestsBattleType,
        order: state.invites.sort.isAsc ? order : `-${order}`,
        offset: (state.invites.page - 1) * limit,
        season_id: clanSeasonType ? currentSeason : undefined,
        limit,
      };

      const json = await get<UpdateInviteType>(url, query);

      if (json.error) {
        dispatch(actionsInvites.updateError({ error: json.error }));
        return;
      }

      const activeInvitesCount = json?._meta_?.total || currentAccount.activeInvitesCount;
      dispatch(currentAccountActions.updateAccountInfo({ activeInvitesCount }));

      dispatch(actionsInvites.updateInvites(json));
    } catch (error) {
      dispatch(
        actionsInvites.updateError({
          error: t('Произошла ошибка. Повторите попытку позже'),
        }),
      );
    } finally {
      loader?.stop(dispatch);
    }
  };

const processSendError = (
  dispatch: IAppDispatch,
  error: IApiSendInviteError | null,
  account: IAccount,
  currentClan: ClanInfoType | ClanPreloaded,
  shouldFetchInvites: boolean,
) => {
  const reason = error?.title?.toLowerCase();

  if (reason === 'account_does_not_meet_requirements') {
    sendSendInviteAlreadySentNotification(account.name, currentClan);
    if (shouldFetchInvites) {
      void dispatch(fetchInvites());
    }
    return;
  }

  if (reason === 'account_already_in_clan') {
    sendSendInvitePlayerInOtherClanErrorNotification(account.name);
    return;
  }

  if (reason === 'too_many_invites') {
    sendSendInviteDailyInvitesLimitReachedErrorNotification(account.name);
    return;
  }
  if (reason === 'account_banned') {
    sendSendInvitePlayerHasPermanentBanErrorNotification(account.name);
    return;
  }

  if (reason === 'insufficient_permissions' || reason === 'permission_denied') {
    if (!currentClan) {
      sendSendInvitePermissionsErrorNotification(account.name);
    } else {
      sendSendInviteYouAreNotInClanNotification(account.name, currentClan);
    }

    return;
  }

  if (reason === 'clan_is_full') {
    sendSendInviteClanIsFullErrorNotification(account.name, currentClan);
    return;
  }

  sendSendInviteErrorWithPlayerInfoNotification(account.name);
};

export const sendInvite =
  (account: IAccount, shouldFetchInvites = false): AppAsyncThunk<void> =>
  async (dispatch, getState) => {
    const state = getState();
    const currentClan = currentClanSelector(state);

    if (!currentClan) {
      return;
    }

    const loader = new Loader();
    loader.start(dispatch);

    try {
      const url = urls.invites;
      const payload = {
        recipient_id: account.id,
        source: 'clans_portal.recruitstation',
      };

      const response = await post<IApiSendInviteResponse & IApiSendInviteError>(url, payload);

      if (response.status === 'error' || response.error || response.errors) {
        processSendError(dispatch, response, account, currentClan, shouldFetchInvites);
        return;
      }

      sendSendInviteNotification(account.name, currentClan);
      if (shouldFetchInvites) {
        void dispatch(fetchInvites());
      }
    } catch (error: unknown) {
      const axiosError = error as AxiosCustomError<IApiSendInviteError>;
      processSendError(dispatch, axiosError.data, account, currentClan, shouldFetchInvites);
    } finally {
      loader.stop(dispatch);
    }
  };

export const cancelInvite =
  (inviteId: number, withGlobalSpinner = false): AppAsyncThunk<void> =>
  async (dispatch) => {
    dispatch(actionsApplications.startProcessing([inviteId]));

    const loader = withGlobalSpinner ? new Loader() : null;
    loader?.start(dispatch);

    try {
      const url = `${urls.invites}${inviteId}/`;
      const payload = { status: REQUEST_STATUSES.DELETED };

      const response = await patch<IApiDeclineApplicationResponse>(url, payload);

      if (response.status === 'error' || response.error || response.errors) {
        sendCancelInviteErrorNotification();
        dispatch(actionsInvites.processingFailed(inviteId));
        return;
      }

      sendCancelInviteNotification();
      void dispatch(fetchInvites());
      dispatch(actionsApplications.processingSuccess([inviteId]));
    } catch {
      sendCancelInviteErrorNotification();
      dispatch(actionsInvites.processingFailed(inviteId));
    } finally {
      loader?.stop(dispatch);
    }
  };

export const joinClanFromSearchResults =
  (clan: ClanInSearch, applicationSource: ENTRY_SOURCE = ENTRY_SOURCE.CLAN_SEARCH): AppAsyncThunk =>
  async (dispatch) => {
    const invite = await fetchActiveInviteFromClan(clan.id, urls.activeInvite);

    if (!invite) {
      dispatch(showSendApplicationDialog(clan, applicationSource));
      return;
    }

    dispatch(showInviteAcceptDialog(invite));
  };

const fetchActiveInviteFromClan = async (clanId: number, url: string) => {
  try {
    const response = await get<IApiGetRecruitmentActiveInviteResponse>(url.replace('{clan_id}', String(clanId)));
    if (!response || response.error) {
      return false;
    }

    return response._meta_ && response[response._meta_.model];
  } catch (error) {
    return false;
  }
};
